"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  GammaCurve,
  TripBars,
} from "@/components/tools/physics-fields/relativity-diagrams";
import {
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  unitOptions,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import type { TIME_DILATION_CALCULATOR_STRINGS } from "@/components/tools/time-dilation-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-fields/constants";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  type RatioKnown,
  type RelativityError,
  solveDilation,
  solveSpeed,
  solveTrip,
} from "@/lib/tool-kit/physics-fields/relativity";
import {
  dilationWorking,
  speedWorking,
  tripWorking,
} from "@/lib/tool-kit/physics-fields/relativity-working";
import {
  DISTANCE_UNITS,
  findUnit,
  fromBase,
  RELATIVISTIC_SPEED_UNITS,
  TIME_UNITS,
  type UnitDef,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof TIME_DILATION_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

function Failure({
  error,
  strings,
}: {
  error: RelativityError;
  strings: Strings;
}) {
  const messages: Record<RelativityError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "negative-speed": strings.errorNegativeSpeed,
    "light-speed": strings.errorLightSpeed,
    "gamma-below-one": strings.errorGamma,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

/** A value in the unit chosen for its box, with the unit's symbol. */
function shown(
  units: readonly UnitDef[],
  unitId: string,
  value: number,
  fmt: Parameters<typeof formatSig>[0],
  sig: number
): string {
  return `${formatSig(fmt, fromBase(units, unitId, value), sig)} ${findUnit(units, unitId).symbol}`;
}

/* ── Time dilation and length contraction ─────────────────────────────────── */

type DilationKey = "v" | "t" | "l";

const DILATION_EXAMPLES: Record<
  string,
  { text: Record<DilationKey, string>; unit: Record<DilationKey, string> }
> = {
  fast: {
    text: { v: "0.8", t: "1", l: "4" },
    unit: { v: "c", t: "yr", l: "ly" },
  },
  muon: {
    text: { v: "98", t: "2.197", l: "" },
    unit: { v: "%c", t: "us", l: "km" },
  },
  gps: {
    text: { v: "3.874", t: "1", l: "" },
    unit: { v: "km/s", t: "d", l: "km" },
  },
  jet: {
    text: { v: "900", t: "10", l: "" },
    unit: { v: "km/h", t: "h", l: "km" },
  },
};

const DILATION_TIME_UNITS: readonly UnitDef[] = [
  { id: "us", symbol: "µs", factor: 1e-6 },
  ...TIME_UNITS,
];

export function DilationPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("fast");
  const [text, setText] = useState<Record<DilationKey, string>>(
    DILATION_EXAMPLES.fast.text
  );
  const [unit, setUnit] = useState<Record<DilationKey, string>>(
    DILATION_EXAMPLES.fast.unit
  );

  const speed = readQuantity(text.v, RELATIVISTIC_SPEED_UNITS, unit.v);
  const properTime = readQuantity(text.t, DILATION_TIME_UNITS, unit.t);
  const properLength = readQuantity(text.l, DISTANCE_UNITS, unit.l);
  const outcome = solveDilation(speed, properTime ?? 0, properLength ?? 0);
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<DilationKey>[] = [
    {
      key: "v",
      label: strings.speed,
      units: RELATIVISTIC_SPEED_UNITS,
      solved: false,
      hint: strings.speedHint,
    },
    {
      key: "t",
      label: strings.properTime,
      units: DILATION_TIME_UNITS,
      solved: false,
      hint: strings.properTimeHint,
    },
    {
      key: "l",
      label: strings.properLength,
      units: DISTANCE_UNITS,
      solved: false,
      hint: strings.properLengthHint,
    },
  ];
  const names = strings.dilationExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = DILATION_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
    }
  };

  return (
    <>
      <SelectField
        id="td-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(DILATION_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="td"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.gamma}
                value={formatSig(fmt, value.gamma, Math.max(sig, 5))}
              />
              <StatCard
                label={strings.beta}
                value={formatSig(fmt, value.beta, Math.max(sig, 5))}
              />
              <StatCard
                label={strings.gammaMinusOne}
                value={formatSig(fmt, value.excess, sig)}
              />
              {properTime === null ? null : (
                <StatCard
                  label={strings.dilatedTime}
                  value={shown(
                    DILATION_TIME_UNITS,
                    unit.t,
                    value.dilatedTime,
                    fmt,
                    sig
                  )}
                />
              )}
              {properTime === null ? null : (
                <StatCard
                  label={strings.drift}
                  value={`${formatSig(fmt, value.drift, sig)} s`}
                />
              )}
              {properLength === null ? null : (
                <StatCard
                  label={strings.contractedLength}
                  value={shown(
                    DISTANCE_UNITS,
                    unit.l,
                    value.contractedLength,
                    fmt,
                    sig
                  )}
                />
              )}
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={dilationWorking(
                value,
                fmt,
                sig,
                properTime !== null,
                properLength !== null
              )}
            />
            <DiagramFrame caption={strings.curveCaption}>
              <GammaCurve
                beta={value.beta}
                description={strings.curveDescription}
                gamma={value.gamma}
                id="td-curve"
                label={strings.curveLabel}
                markerText={`γ = ${formatSig(fmt, value.gamma, Math.min(sig, 4))}`}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── Speed from a ratio ───────────────────────────────────────────────────── */

type SpeedKey = "a" | "b";

export function SpeedPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [known, setKnown] = useState<RatioKnown>("time");
  const [text, setText] = useState<Record<SpeedKey, string>>({
    a: "10",
    b: "6",
  });
  const [unit, setUnit] = useState<Record<SpeedKey, string>>({
    a: "yr",
    b: "yr",
  });
  const [out, setOut] = useState("c");

  const gammaOnly = known === "gamma";
  const unitsFor = known === "length" ? DISTANCE_UNITS : TIME_UNITS;
  const ratioUnits: readonly UnitDef[] = gammaOnly
    ? [{ id: "x", symbol: "×", factor: 1 }]
    : unitsFor;
  const a = readQuantity(text.a, ratioUnits, gammaOnly ? "x" : unit.a);
  const b = gammaOnly ? null : readQuantity(text.b, unitsFor, unit.b);
  const outcome = solveSpeed(known, a, b);
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<SpeedKey>[] = (() => {
    if (known === "gamma") {
      return [
        {
          key: "a",
          label: strings.gammaInput,
          units: ratioUnits,
          solved: false,
          hint: strings.gammaHint,
        },
      ];
    }
    if (known === "time") {
      return [
        {
          key: "a",
          label: strings.dilatedTimeInput,
          units: unitsFor,
          solved: false,
        },
        {
          key: "b",
          label: strings.properTimeInput,
          units: unitsFor,
          solved: false,
        },
      ];
    }
    return [
      {
        key: "a",
        label: strings.properLengthInput,
        units: unitsFor,
        solved: false,
      },
      {
        key: "b",
        label: strings.contractedLengthInput,
        units: unitsFor,
        solved: false,
      },
    ];
  })();
  const changeKnown = (next: RatioKnown) => {
    setKnown(next);
    if (next === "gamma") {
      setText({ a: "2", b: "" });
    } else if (next === "time") {
      setText({ a: "10", b: "6" });
      setUnit({ a: "yr", b: "yr" });
    } else {
      setText({ a: "4", b: "2.4" });
      setUnit({ a: "ly", b: "ly" });
    }
  };

  return (
    <>
      <PillGroup
        label={strings.knownLabel}
        name="td-known"
        onChange={changeKnown}
        options={[
          { value: "time", label: strings.knownTime },
          { value: "length", label: strings.knownLength },
          { value: "gamma", label: strings.knownGamma },
        ]}
        value={known}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="td-speed"
        onText={(key, v) => setText({ ...text, [key]: v })}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <SelectField
        id="td-speed-out"
        label={strings.showSpeedIn}
        onChange={setOut}
        options={unitOptions(RELATIVISTIC_SPEED_UNITS)}
        value={out}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.speedResult}
                value={shown(
                  RELATIVISTIC_SPEED_UNITS,
                  out,
                  value.speed,
                  fmt,
                  Math.max(sig, 5)
                )}
              />
              <StatCard
                label={strings.beta}
                value={formatSig(fmt, value.beta, Math.max(sig, 6))}
              />
              <StatCard
                label={strings.gamma}
                value={formatSig(fmt, value.gamma, Math.max(sig, 5))}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={speedWorking(known, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.curveCaption}>
              <GammaCurve
                beta={value.beta}
                description={strings.curveDescription}
                gamma={value.gamma}
                id="td-speed-curve"
                label={strings.curveLabel}
                markerText={`γ = ${formatSig(fmt, value.gamma, Math.min(sig, 4))}`}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── A round trip ─────────────────────────────────────────────────────────── */

type TripKey = "d" | "v";

const TRIP_EXAMPLES: Record<
  string,
  { text: Record<TripKey, string>; unit: Record<TripKey, string> }
> = {
  four: { text: { d: "4", v: "0.8" }, unit: { d: "ly", v: "c" } },
  proxima: { text: { d: "4.25", v: "0.9" }, unit: { d: "ly", v: "c" } },
  sirius: { text: { d: "8.6", v: "0.99" }, unit: { d: "ly", v: "c" } },
  vega: { text: { d: "25", v: "0.999" }, unit: { d: "ly", v: "c" } },
};

export function TripPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("four");
  const [text, setText] = useState<Record<TripKey, string>>(
    TRIP_EXAMPLES.four.text
  );
  const [unit, setUnit] = useState<Record<TripKey, string>>(
    TRIP_EXAMPLES.four.unit
  );
  const [timeUnit, setTimeUnit] = useState("yr");

  const outcome = solveTrip(
    readQuantity(text.d, DISTANCE_UNITS, unit.d),
    readQuantity(text.v, RELATIVISTIC_SPEED_UNITS, unit.v)
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<TripKey>[] = [
    {
      key: "d",
      label: strings.tripDistance,
      units: DISTANCE_UNITS,
      solved: false,
      hint: strings.tripDistanceHint,
    },
    {
      key: "v",
      label: strings.speed,
      units: RELATIVISTIC_SPEED_UNITS,
      solved: false,
    },
  ];
  const names = strings.tripExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = TRIP_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
    }
  };
  const time = (seconds: number) =>
    shown(TIME_UNITS, timeUnit, seconds, fmt, sig);

  return (
    <>
      <SelectField
        id="td-trip-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(TRIP_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="td-trip"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <SelectField
        id="td-trip-time-unit"
        label={strings.showTimesIn}
        onChange={setTimeUnit}
        options={unitOptions(TIME_UNITS)}
        value={timeUnit}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.ageDifference}
                value={time(value.ageDifference)}
              />
              <StatCard
                label={strings.earthTime}
                value={time(value.earthTime)}
              />
              <StatCard
                label={strings.travellerTime}
                value={time(value.travellerTime)}
              />
              <StatCard
                label={strings.gamma}
                value={formatSig(fmt, value.gamma, Math.max(sig, 5))}
              />
              <StatCard
                label={strings.contractedDistance}
                value={shown(
                  DISTANCE_UNITS,
                  unit.d,
                  value.contractedDistance,
                  fmt,
                  sig
                )}
              />
              <StatCard
                label={strings.speedKmS}
                value={`${formatSig(fmt, (value.beta * SPEED_OF_LIGHT) / 1000, sig)} km/s`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={tripWorking(value, fmt, sig)}
            />
            <DiagramFrame caption={strings.tripCaption}>
              <TripBars
                description={strings.tripDescription}
                earthLabel={strings.earthClock}
                earthText={time(value.earthTime)}
                id="td-trip-bars"
                label={strings.tripLabel}
                ratio={value.travellerTime / value.earthTime}
                travellerLabel={strings.travellerClock}
                travellerText={time(value.travellerTime)}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
